/**
 * SwipeableListItem — Wraps a list item card and reveals action buttons
 * behind it as the user swipes left or right on touch devices.
 *
 * Left swipe reveals trailing actions (e.g. Delete, Reject).
 * Right swipe reveals leading actions (e.g. Approve, Process).
 *
 * Visual design:
 * - Actions are absolutely positioned behind the foreground content
 * - Foreground content translates with the drag and springs back on release
 * - Action backgrounds use color-coded gradients for quick recognition
 *
 * Non-touch devices: renders children directly (no swipe behavior).
 */

'use client';

import { useRef, useState, useCallback, useEffect } from 'react';

export interface SwipeAction {
  /** Icon element to render */
  icon: React.ReactNode;
  /** Label text shown under the icon */
  label: string;
  /** Background color class (e.g. 'bg-emerald-500') */
  bgClass: string;
  /** Callback when the action is triggered */
  onClick: () => void;
}

interface SwipeableListItemProps {
  /** The list item content (card) */
  children: React.ReactNode;
  /** Actions revealed when swiping right (leading side, left-aligned) */
  leadingActions?: SwipeAction[];
  /** Actions revealed when swiping left (trailing side, right-aligned) */
  trailingActions?: SwipeAction[];
  /** Swipe threshold in px to trigger an action. Default: 80 */
  threshold?: number;
  /** Maximum drag offset in px. Default: 120 */
  maxDrag?: number;
  /** Whether swipe is enabled (e.g. disabled for deleted items) */
  enabled?: boolean;
  /** Additional className for the outer container */
  className?: string;
  /** Click handler for the item (passed through to foreground) */
  onClick?: () => void;
}

export function SwipeableListItem({
  children,
  leadingActions = [],
  trailingActions = [],
  threshold = 80,
  maxDrag = 120,
  enabled = true,
  className = '',
  onClick,
}: SwipeableListItemProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const tracking = useRef(false);
  const lockedDir = useRef<'h' | 'v' | null>(null);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Keep action callbacks in refs
  const leadingRef = useRef(leadingActions);
  const trailingRef = useRef(trailingActions);
  useEffect(() => {
    leadingRef.current = leadingActions;
    trailingRef.current = trailingActions;
  }, [leadingActions, trailingActions]);

  // Only enable swipe if there are actions and touch is available
  const hasActions = leadingActions.length > 0 || trailingActions.length > 0;
  const swipeEnabled = enabled && hasActions;

  const reset = useCallback(() => {
    setDragX(0);
    setIsDragging(false);
    tracking.current = false;
    lockedDir.current = null;
  }, []);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!swipeEnabled) return;
      const touch = e.touches[0];
      startX.current = touch.clientX;
      startY.current = touch.clientY;
      tracking.current = true;
      lockedDir.current = null;
      setIsDragging(true);
    },
    [swipeEnabled],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!swipeEnabled || !tracking.current) return;
      const touch = e.touches[0];
      const dx = touch.clientX - startX.current;
      const dy = touch.clientY - startY.current;

      if (lockedDir.current === null) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          lockedDir.current = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        }
      }

      if (lockedDir.current === 'v') return;

      if (Math.abs(dy) > 60) {
        tracking.current = false;
        setDragX(0);
        setIsDragging(false);
        return;
      }

      // Only allow drag in directions that have actions
      let clamped = dx;
      if (dx < 0 && trailingActions.length === 0) clamped = 0;
      if (dx > 0 && leadingActions.length === 0) clamped = 0;
      clamped = Math.max(-maxDrag, Math.min(maxDrag, clamped));
      setDragX(clamped);
    },
    [swipeEnabled, maxDrag, trailingActions.length, leadingActions.length],
  );

  const handleTouchEnd = useCallback(() => {
    if (!swipeEnabled || !tracking.current) {
      setDragX(0);
      setIsDragging(false);
      return;
    }
    tracking.current = false;
    setIsDragging(false);

    if (dragX <= -threshold && trailingRef.current.length > 0) {
      // Trigger the first trailing action (primary)
      trailingRef.current[0]?.onClick();
    } else if (dragX >= threshold && leadingRef.current.length > 0) {
      // Trigger the first leading action (primary)
      leadingRef.current[0]?.onClick();
    }
    setDragX(0);
    lockedDir.current = null;
  }, [swipeEnabled, dragX, threshold]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !swipeEnabled) return;

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, swipeEnabled]);

  // If no swipe actions, render children directly
  if (!swipeEnabled) {
    return (
      <div className={className} onClick={onClick}>
        {children}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`}>
      {/* Behind: leading actions (revealed on swipe right) */}
      {leadingActions.length > 0 && (
        <div className="absolute inset-y-0 left-0 flex items-stretch">
          {leadingActions.map((action, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
                reset();
              }}
              className={`flex flex-col items-center justify-center px-4 ${action.bgClass} text-white`}
              style={{ minWidth: 72 }}
            >
              {action.icon}
              <span className="mt-1 text-[10px] font-medium leading-tight">{action.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Behind: trailing actions (revealed on swipe left) */}
      {trailingActions.length > 0 && (
        <div className="absolute inset-y-0 right-0 flex items-stretch">
          {trailingActions.map((action, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
                reset();
              }}
              className={`flex flex-col items-center justify-center px-4 ${action.bgClass} text-white`}
              style={{ minWidth: 72 }}
            >
              {action.icon}
              <span className="mt-1 text-[10px] font-medium leading-tight">{action.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Foreground: the actual card content */}
      <div
        className="relative bg-inherit"
        style={{
          transform: `translateX(${dragX}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
        onClick={(e) => {
          if (Math.abs(dragX) > 5) {
            e.preventDefault();
            e.stopPropagation();
            reset();
            return;
          }
          onClick?.();
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default SwipeableListItem;
